import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Select, MenuItem, FormControl, Button, ToggleButtonGroup, ToggleButton, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CscMeterRow, { meterName } from './cscMeterRow';
import CscLoadProfileChart from './cscLoadProfileChart';
import { buildSyntheticAnalysis, loadShapeFromAnalysis } from '../../utils/cscSynthProfile';
import {
    omForKwp, warrantyForKwp, warrantyYears, warrantyHasCleaning,
    WARRANTY_TERM_OPTIONS, cleaningOptionsFor, composeWarranty, warrantyCleaningTimes, warrantyTermOnly,
    DEFAULT_WARRANTY_TERM,
} from '../../utils/cscSizing';
import { sstPortion, sstOn } from '../../utils/sst';
import { ccFromCash } from '../../utils/ccPricing';
import { setAfaOn } from '../../hooks/useRscPricing';
import { isMvTariff } from '../../hooks/useCscBillProposal';
import useCscProposalMath, { defaultSareTariffByKwp } from '../../hooks/useCscProposalMath';
import { buildCscVarMap, generateCscProposalPdf, todayDMY, plus30DMY, MY_STATE_GHI, cscSubstitute, postProcessCscProposal } from './cscProposalEngine';
import { buildCscWhatsAppMsg } from './cscWhatsApp';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { generateCommercialQuotation2, generateCommercialAtap, generateCommercialDeclaration, generateCommercialInsurance2, generateCommercialBooking, generateCommercialAll, getSalesTitle } from '../../api/formGeneratorApi';
import { getAllAgentGroups, getAgentsByGroupId, getAgentDetailsByAgentId } from '../../api/userApi';
import { useUser } from '../../context/userContext';
import handleAgentName from '../../utils/userUtils';
import { createSales } from '../../api/salesApi';

// ─────────────────────────────────────────────────────────────────────────
// Multi-Meter CSC tab — ONE customer, N meters (projects). Each meter is sized
// by the EXACT single-meter CSC Option-B calc (see cscMeterRow.jsx), and every
// cost / spec is COMBINED. Section-2 outputs (Proposal / Quotation / WhatsApp)
// reuse the SAME builders as the single-meter Combined tab, fed the combined
// totals. No CSC math is reinvented — the per-meter result comes straight from
// CscMeterRow which runs cscCombinedTab's logic verbatim.
// ─────────────────────────────────────────────────────────────────────────

const PP = "'Poppins', sans-serif";
// sessionStorage key for the Multi-Meter tab draft — keeps filled data across page
// navigation (cleared on tab close or via the Clear Data button). Mirrors the
// Combined tab's csc_draft_v1; separate key so the two tabs don't clobber each other.
// Free one-time cleaning is included from 30 kWp up. Below that the option is not offered
// at all — an option you have to remember not to pick is a promise somebody eventually
// makes by accident. (user 2026-10-14)
// warrantyOptionsFor / warrantyForKwp now live in ../../utils/cscSizing, shared with
// the multi-meter twin and the quotation preview. (user 2026-10-26)

const CSC_MM_DRAFT_KEY = 'csc_multimeter_draft_v1';
const tr = (lang, en, zh, ms) => (lang === 'zh' ? zh : (lang === 'ms' && ms ? ms : en));
// Meter label uses LETTERS (A, B, C…) keyed off the meter's real index.
const meterLetter = (i) => String.fromCharCode(65 + (Number(i) || 0));
const PHOTO_API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api$/, '');
const absPhoto = (u) => (u ? `${PHOTO_API_BASE}${u}` : '');

const COLORS = {
    primary: '#1A8CFF', dark: '#0F62CC', light: '#E8F4FF', bg: '#F8FAFC', card: '#FFFFFF',
    text: '#0F172A', sub: '#64748B', border: '#E2E8F0', success: '#10B981', warning: '#F59E0B',
};

const fmtRM = (n) => 'RM ' + Math.round(Number(n) || 0).toLocaleString('en-MY');
// SST shown to 2 decimals (exact, not whole-ringgit) — user 2026-08-04.
const fmtRM2 = (n) => 'RM ' + Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Section header — mirrors cscCombinedTab.SectionHeader.
const SectionHeader = ({ title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, mb: 1 }}>
        <Box sx={{ width: 4, height: 24, bgcolor: '#1A8CFF', borderRadius: 1 }} />
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, letterSpacing: '0.02em' }}>{title}</Typography>
        <Box sx={{ flex: 1, height: 1, bgcolor: '#E2E8F0' }} />
    </Box>
);

function Field({ label, value, onChange, onBlur, type = 'text', disabled, required, options }) {
    const isSelect = Array.isArray(options);
    // A saved sale may carry an old free-text warranty value not in the option list —
    // include it so the dropdown still displays it (user can then switch to a standard one).
    const opts = isSelect && value && !options.includes(value) ? [value, ...options] : options;
    return (
        <Box>
            <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>
                {label}{required && <span style={{ color: '#EF4444', marginLeft: 2 }}>*</span>}
            </Typography>
            <TextField fullWidth size="small" type={type} value={value} disabled={disabled}
                select={isSelect}
                onChange={e => onChange && onChange(e.target.value)}
                onBlur={e => onBlur && onBlur(e.target.value)}
                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }}>
                {isSelect && opts.map(o => (
                    <MenuItem key={o} value={o} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>{o}</MenuItem>
                ))}
            </TextField>
        </Box>
    );
}

const CC_FACTORS = { 24: 0.935, 36: 0.925, 60: 0.9 };

// Stable, deterministic per-meter id so React reconciles each <CscMeterRow> by
// identity (not array position). Removing a middle meter must NOT carry a
// previous meter's internal battery/lastSigRef state onto a different meter.
let _mid = 0;
const nextMeterId = () => ++_mid;

export default function CscMultiMeterTab({ sharedPhaseMode = '3phase', setSharedPhaseMode, lang = 'en' }) {
    const { user } = useUser();
    const navigate = useNavigate();

    // ── Shared inputs (same as CSC Section 1, shared across all meters) ──
    const [operatingDays, setOperatingDays] = useState(7);
    const [opStart, setOpStart] = useState(9);
    const [opEnd, setOpEnd] = useState(17);
    const [dayUsagePct, setDayUsagePct] = useState('');
    const [brand, setBrand] = useState('goodwe');
    // AFA on/off (default OFF — No AFA, per user 2026-07-02; shared with RSC/Combined via the
    // getAfaOn singleton read by calcCommercialLvBill). Applies to every meter's bill breakdown.
    const [afaOn, setAfaOnState] = useState(false);
    useEffect(() => { setAfaOn(afaOn); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Meters — start with 1; +Add meter; ✕ remove (min 1). ──
    const newMeter = () => ({ id: nextMeterId(), bill: '', maxDemand: '', tariffRate: 0.515 });
    const [meters, setMeters] = useState([newMeter()]);
    // An MV meter's bill is CALCULATED from its readings, so it must carry AFA like the real TNB
    // bill. One MV meter switches AFA on for the tab; the agent can still turn it off.
    // (user 2026-09-23)
    const _anyMv = meters.some(m => isMvTariff(Number(m.tariffRate) || 0));
    useEffect(() => {
        if (_anyMv) { setAfaOn(true); setAfaOnState(true); }
    }, [_anyMv]); // eslint-disable-line react-hooks/exhaustive-deps
    const [results, setResults] = useState({});   // index → result (from CscMeterRow)
    const [lpAnalyses, setLpAnalyses] = useState({});   // index → uploaded load-profile analysis
    const onMeterLpAnalysis = (index, analysis) => setLpAnalyses(prev => ({ ...prev, [index]: analysis }));

    // Clear Data — wipe the saved draft and reload so every tab starts fresh.
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    const doClearData = () => { try { sessionStorage.removeItem(CSC_MM_DRAFT_KEY); } catch {} window.location.reload(); };

    const addMeter = () => setMeters(prev => [...prev, newMeter()]);
    const removeMeter = (index) => {
        setMeters(prev => prev.filter((_, i) => i !== index));
        setResults(prev => {
            // Re-key results after removal so indices stay contiguous.
            const arr = Object.keys(prev).sort((a, b) => a - b).filter(k => Number(k) !== index).map(k => prev[k]);
            const next = {}; arr.forEach((r, i) => { if (r) next[i] = { ...r, index: i }; }); return next;
        });
    };
    // MERGE rather than replace. Callers that pass a whole meter object behave exactly as
    // before (spreading it over the previous value is a no-op), but a caller may now send a
    // partial patch without wiping the keys it left out — which is what lets CscMeterRow
    // mirror up its own inputs without having to hold a fresh copy of the whole meter.
    const changeMeter = (index, patch) => setMeters(prev => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
    const onMeterResult = (index, result) => setResults(prev => (JSON.stringify(prev[index]) === JSON.stringify(result) ? prev : { ...prev, [index]: result }));

    // Ordered list of present results (only meters with a computed result + bill).
    const resultList = meters.map((_, i) => results[i]).filter(r => r && r.kwp > 0 && r.bill > 0);

    // ── COMBINED totals ──
    const combined = (() => {
        const sum = (sel) => resultList.reduce((s, r) => s + (Number(sel(r)) || 0), 0);
        const kwp = +sum(r => r.kwp).toFixed(2);
        const panels = sum(r => r.panels);
        const inverterKw = +sum(r => r.inverterKw).toFixed(1);
        const battKwh = sum(r => r.battKwh);
        // One brand for the combined battery line: Huawei only when EVERY meter with a
        // battery is Huawei; any GoodWe (or a mix) stays GoodWe. (user 2026-09-21)
        const _battRows = resultList.filter(r => Number(r.battKwh) > 0);
        const battBrand = _battRows.length > 0 && _battRows.every(r => r.battBrand === 'huawei') ? 'huawei' : 'goodwe';
        const panel = sum(r => r.panel);
        const panelNet = sum(r => r.panelNet);
        const batteryCost = sum(r => r.batteryCost);
        const cash = sum(r => r.cash);
        // Derived from the COMBINED SST-inclusive total rather than summing each meter's
        // instalment - summing compounds the per-meter roundUp10 and drifts from the figure
        // the customer is quoted. (user 2026-08-21)
        const _ccBase = cash + sstOn(cash);
        const cc24 = ccFromCash(_ccBase, 24);
        const cc36 = ccFromCash(_ccBase, 36);
        const cc60 = ccFromCash(_ccBase, 60);
        const savingMo = sum(r => r.savingMo);
        // Maintenance is priced per-kWp off the COMBINED system, not summed per meter
        // (user 2026-08-20). One site under one O&M contract: three meters of 16.9 /
        // 28.6 / 21.45 kWp are a single 66.95 kWp system on the RM4,500 tier, not
        // 2,600 + 3,500 + 3,500 = RM9,600. Same omForKwp table the single-meter
        // Combined tab and each meter row already use.
        const om = omForKwp(kwp, 'premium');
        // Catalogue rows + the flat per-meter Add-on. Summed so the PROPOSAL can show
        // them: the card, the WhatsApp message and the sale all carry add-ons already
        // (they live inside each meter's `cash`), but the PDF was built from
        // panelNet + battery only and quoted RM3,000 short on Genting. (user 2026-08-21)
        const addons = sum(r => r.addons);
        const bill = sum(r => r.bill);
        // Combined CASH 80/20 + SST (matches the per-meter quotation method).
        const c80 = Math.round(cash * 0.80);
        const c20 = sstPortion(cash);
        const cSst = sstOn(cash);
        const grandTotal = c80 + c20 + cSst;
        // Combined ROI = total cost ÷ total annual saving.
        const roiYears = (savingMo * 12) > 0 ? Math.round((cash / (savingMo * 12)) * 100) / 100 : null;
        const savingPct = bill > 0 ? savingMo / bill : null;
        // Combined energy breakdown (summed across meters) for the combined chart + breakdown.
        const monthlyKwh = sum(r => r.monthlyKwh);
        const generated = sum(r => r.generated);
        const selfConsumed = sum(r => r.selfConsumed);
        const exported = sum(r => r.exported);
        const curtailed = sum(r => r.curtailed);
        const importAfterSolar = sum(r => r.importAfterSolar);
        const battCharge = sum(r => r.battCharge);
        const battUsed = sum(r => r.battUsed);
        const exportCredit = sum(r => r.exportCredit);
        const selfUseSaving = sum(r => r.selfUseSaving);
        const anyAtap = resultList.some(r => r.mode !== 'selco');   // ATAP if any meter exports
        // `addons` MUST be returned. It was summed above but left out of this object, so
        // every consumer read `combined.addons` as undefined: the proposal printed
        // "+ Add-on RM 0" (Number(undefined) || 0) and mathSystemCost silently became NaN.
        // The catalogue add-ons were charged in `cash` the whole time, so the customer's
        // WhatsApp total and the PDF disagreed by exactly the add-on value. (bug 2026-08-26)
        return { kwp, panels, inverterKw, battKwh, battBrand, panel, panelNet, batteryCost, addons, cash, cc24, cc36, cc60, savingMo, om, bill, c80, c20, cSst, grandTotal, roiYears, savingPct,
            monthlyKwh, generated, selfConsumed, exported, curtailed, importAfterSolar, battCharge, battUsed, exportCredit, selfUseSaving, anyAtap };
    })();

    const anyResult = resultList.length > 0;

    // Combined day/night baseline (mirrors cscMeterRow._offHour) + a COMBINED synthetic
    // load-profile analysis (all meters summed) for the combined chart. (user 2026-07-25)
    const _combinedOffHour = (() => {
        const oh = Math.max(0, opEnd - opStart);
        const computed = oh <= 0 ? 100 : Math.max(0, Math.min(100, Math.round(Math.max(0, Math.min(opEnd, 18) - Math.max(opStart, 8)) / oh * 100)));
        const eff = String(dayUsagePct).trim() !== '' ? Math.max(0, Math.min(100, parseFloat(dayUsagePct) || 0)) : computed;
        return Math.max(0, Math.min(1, (100 - eff) / 100));
    })();
    // If ANY meter uploaded a load profile, use its real pattern for the WHOLE combined
    // chart (meters without one adapt it, scaled to their usage). Else the flat synthetic.
    const _uploadedLp = Object.values(lpAnalyses).find(Boolean) || null;
    const _loadShape = _uploadedLp ? loadShapeFromAnalysis(_uploadedLp) : null;
    const combinedSynth = (combined.kwp > 0 && combined.monthlyKwh > 0)
        ? buildSyntheticAnalysis({ kwp: combined.kwp, monthlyKwh: combined.monthlyKwh, operatingDays, opStart, opEnd, offHourBaselinePct: _combinedOffHour, mode: combined.anyAtap ? 'atap' : 'selco', batteryKwh: combined.battKwh, loadShape: _loadShape })
        : null;
    // DISPLAY-ONLY tallying values for the on-screen Combined Summary table.
    // The document/sale uses combined.cSst = round(round(Σcash*0.20)*0.06), which is
    // internally consistent but may differ by a ringgit or two from the SUM of the
    // per-meter SST cells shown in the table. To make the on-screen row visually add
    // up, the table derives its SST + Grand Total from the SAME per-meter basis it
    // displays. This does NOT change combined.cSst / combined.grandTotal (the values
    // that flow into the Quotation / Sale stay untouched).
    const perMeterSst = (r) => sstOn(r.cash);
    const dispSst = resultList.reduce((s, r) => s + perMeterSst(r), 0);
    const dispGrandTotal = resultList.reduce((s, r) => s + r.cash + perMeterSst(r), 0);
    // Breakdown string helper: "30 + 40 = 70 kWp".
    const breakdown = (sel, total, unit) => {
        if (resultList.length <= 1) return `${total}${unit ? ' ' + unit : ''}`;
        return `${resultList.map(r => sel(r)).join(' + ')} = ${total}${unit ? ' ' + unit : ''}`;
    };

    // ── Project Info / Financing / Prepared-By (mirrors cscCombinedTab) ──
    // Combined-summary table -> PNG. Same html2canvas settings as the bill
    // calculator's shared photo (scale 2, white ground, CORS on).
    const summaryRef = useRef(null);
    const [chartSaving, setChartSaving] = useState(false);

    // Combined summary -> .xlsx, read off the rendered table.
    //
    // Walks summaryRef instead of re-declaring the rows: the table is ~20 TableRowCells
    // written inline in the JSX below, and a second copy for the export would drift the
    // first time somebody added a row. Each rendered row is either a full-width GroupRow
    // (one cell) or a data row of 1 + meters + 1 cells, so the shape is read from the
    // row itself rather than assumed.
    const onExportSummaryExcel = () => {
        const el = summaryRef.current;
        if (!el) return;
        const aoa = [];
        for (const row of Array.from(el.children)) {
            const cells = Array.from(row.children);
            if (cells.length <= 1) {
                // Section heading (SYSTEM / SAVINGS & ROI / PRICING…) — keep it as a row
                // so the sheet reads in the same blocks as the screen.
                aoa.push([(row.innerText || '').trim()]);
                continue;
            }
            aoa.push(cells.map((c) => {
                const txt = (c.innerText || '').trim();
                // "RM 104,130" / "57.85 kWp" stay as text: the combined column holds
                // sums like "57.85 + 72.15 + 104 = 234 kWp", so forcing numbers would
                // mangle the one column people most want to read.
                return txt;
            }));
        }
        if (aoa.length === 0) return;

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        // Label column wide enough for "Grand Total (incl SST)"; the rest even.
        ws['!cols'] = [{ wch: 24 }, ...Array(Math.max(0, (aoa[0] || []).length - 1)).fill({ wch: 18 })];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Combined Summary');

        const safe = (customerName || 'Combined Summary').trim().replace(/[\\/:*?"<>|]/g, '-');
        const d = new Date();
        const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        XLSX.writeFile(wb, `${safe} - Combined Summary ${stamp}.xlsx`);
    };

    const onDownloadSummary = async () => {
        const el = summaryRef.current;
        if (!el || chartSaving) return;
        setChartSaving(true);
        const prevOverflowX = el.style.overflowX;
        try {
            // The table scrolls horizontally on narrow screens, so html2canvas would
            // capture only the visible slice and crop the COMBINED column clean off.
            // Unclamp it for the shot and size the canvas to the FULL scroll width.
            el.style.overflowX = 'visible';
            const canvas = await html2canvas(el, {
                scale: 2,
                backgroundColor: '#ffffff',
                useCORS: true,
                width: el.scrollWidth,
                height: el.scrollHeight,
                windowWidth: el.scrollWidth,
            });
            const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
            if (!blob) throw new Error('canvas produced no blob');
            // Same sanitiser and naming shape as the proposal export below,
            // so a customer's files sort together in the downloads folder.
            const safeName = (customerName || 'Customer').replace(/[\/\\:*?"<>|]/g, '').trim();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${safeName} - ${kwpComboLabel}KWP Combined Summary.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch (e) {
            console.error('[CSC] summary download failed', e);
        } finally {
            el.style.overflowX = prevOverflowX;
            setChartSaving(false);
        }
    };

    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [siteState, setSiteState] = useState('');
    const [sitePhotos, setSitePhotos] = useState([]);
    const [includeOutright, setIncludeOutright] = useState(true);
    const [includeSARE, setIncludeSARE] = useState(true);
    // 10-year SARE term — independent toggle (user 2026-08-23).
    const [includeSARE10, setIncludeSARE10] = useState(true);
    const [includeLoan, setIncludeLoan] = useState(true);
    const [includeCA, setIncludeCA] = useState(true);
    const [includeITA, setIncludeITA] = useState(false);
    const [itaTouched, setItaTouched] = useState(false); // user manually toggled GITA
    // GITA available ONLY for combined ≥ 40 kWp — below that forced OFF and greyed out;
    // at/above 40 kWp defaults ON until the user toggles it. (user 2026-07-25)
    useEffect(() => {
        const k = Number(combined.kwp) || 0;
        if (k < 40) { setIncludeITA(false); return; }
        if (itaTouched) return;
        setIncludeITA(true);
    }, [combined.kwp, itaTouched]);
    const [sareIncludeTouched, setSareIncludeTouched] = useState(false); // user manually toggled SARE
    const [sareTariffOverride, setSareTariffOverride] = useState('');
    const [loanPrincipal, setLoanPrincipal] = useState('');
    // SARE default follows combined kWp: ON for ≥40 (and before a system exists), OFF for a real
    // system <40. Skipped once the user manually toggles the checkbox. (user 2026-07-23)
    useEffect(() => { if (sareIncludeTouched) return; const _k = combined.kwp || 0; setIncludeSARE(_k === 0 || _k >= 42); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [combined.kwp, sareIncludeTouched]);

    // What the SARE rate has to recover on top of the panel system, across ALL meters:
    // the batteries and every add-on row. (user 2026-10-14: "any addon including battery")
    const _sareAddOnCost = (combined.batteryCost || 0) + (combined.addons || 0);
    // The tariff field used to sit blank and let the math hook fall back to the kWp tier
    // silently. Now that add-ons move the rate, the number has to be ON SCREEN — an uplift
    // nobody can see is one nobody will trust. Same auto-default as the Combined tab, and
    // the same String() rather than toFixed(2), so an uplift of RM0.015 stays 0.375.
    const [sareTouched, setSareTouched] = useState(false);
    useEffect(() => {
        if (sareTouched) return;
        const k = Number(combined.kwp) || 0;
        if (k < 40) { setSareTariffOverride(''); return; }
        setSareTariffOverride(String(defaultSareTariffByKwp(k, 20, _sareAddOnCost)));
    }, [combined.kwp, _sareAddOnCost, sareTouched]);
    const [loanRate, setLoanRate] = useState('5.0');
    const [loanTermMonths, setLoanTermMonths] = useState('84');
    const [sales, setSales] = useState({ name: '', title: 'Solar Consultant', email: '', whatsapp: '', photoUrl: '' });

    // Commercial-form fields (feed Quotation / ATAP / Declaration / Insurance).
    const [refNo, setRefNo] = useState('');
    const [companyTin, setCompanyTin] = useState('');
    const [companyOffice, setCompanyOffice] = useState('');
    const [companyFax, setCompanyFax] = useState('');
    const [companyEmail, setCompanyEmail] = useState('');
    const [companyWebsite, setCompanyWebsite] = useState('');
    const [picSalutation, setPicSalutation] = useState('');
    const [picName, setPicName] = useState('');
    const [picMykad, setPicMykad] = useState('');
    const [picPosition, setPicPosition] = useState('');
    const [picCitizenship, setPicCitizenship] = useState('');
    const [picMobile, setPicMobile] = useState('');
    const [picEmail, setPicEmail] = useState('');
    const [operationWarranty, setOperationWarranty] = useState(DEFAULT_WARRANTY_TERM);
    // Set once the agent picks a Cleaning themselves; until then it simply follows
    // the system size. (user 2026-10-26: "more than 30kwp got free 1 time cleaning")
    const [cleaningTouched, setCleaningTouched] = useState(false);
    // Combined kWp decides it here — the cleaning is for the installation as a whole.
    useEffect(() => {
        const k = Number(combined?.kwp) || 0;
        if (!(k > 0)) return;
        setOperationWarranty((prev) => warrantyForKwp(prev, k, cleaningTouched));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [combined?.kwp, cleaningTouched]);
    const [defectWarranty, setDefectWarranty] = useState('1 set');
    const [insuranceAmount, setInsuranceAmount] = useState('');

    // Prepared-By picker (Group → Agent → Position) — mirrors cscCombinedTab.
    const [agentGroups, setAgentGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [groupAgents, setGroupAgents] = useState([]);
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const filterAgents = (list) => (list || []).filter(a => !(a.userRoles || []).some(ur => ur.roleName === 'Referral'));
    const proposalLang = lang === 'zh' ? 'cn' : lang === 'ms' ? 'my' : 'en';

    useEffect(() => {
        let c = false;
        getAllAgentGroups().then(r => { if (!c) setAgentGroups(r.data || []); }).catch(() => {});
        return () => { c = true; };
    }, []);
    useEffect(() => {
        if (!user?.userId) return;
        let c = false;
        setSales(s => ({ ...s, name: handleAgentName(user.firstName, user.lastName, user.alias), photoUrl: absPhoto(user.profilePhotoUrl) }));
        getAgentDetailsByAgentId(user.userId)
            .then(res => {
                const gid = res?.data?.groupId;
                if (c || !gid) return;
                setSelectedGroupId(gid);
                return getAgentsByGroupId(gid).then(ar => {
                    if (c) return;
                    setGroupAgents(filterAgents(ar.data));
                    setSelectedAgentId(user.userId);
                });
            })
            .catch(() => {});
        getSalesTitle(user.userId)
            .then(res => { const t = res?.data?.title; if (!c && t) setSales(s => ({ ...s, title: t })); })
            .catch(() => {});
        return () => { c = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const onPickGroup = (gid) => {
        setSelectedGroupId(gid); setSelectedAgentId('');
        getAgentsByGroupId(gid).then(r => setGroupAgents(filterAgents(r.data))).catch(() => setGroupAgents([]));
    };
    const onPickAgent = (uid) => {
        setSelectedAgentId(uid);
        const a = groupAgents.find(x => String(x.userId) === String(uid));
        if (a) {
            const photo = a.profilePhotoUrl || (String(uid) === String(user?.userId) ? user?.profilePhotoUrl : '');
            setSales(s => ({ ...s, name: handleAgentName(a.firstName, a.lastName, a.alias), photoUrl: absPhoto(photo) }));
        }
        getSalesTitle(uid).then(r => { const t = r?.data?.title; setSales(s => ({ ...s, title: t || s.title })); }).catch(() => {});
    };

    // ── Combined proposal math ──
    // useCscProposalMath is PURE & LINEAR in (kwp, systemCost): generation, CA,
    // revenue, loan all scale linearly, so calling it once with the COMBINED kWp +
    // COMBINED capital (panelNet + battery) yields exactly the SUM of each meter's
    // 20-year projection. This is the documented aggregation of the per-meter math.
    // Generation-weighted blended tariff: Σ(kwp_i × tariff_i) / Σ(kwp_i). Generation
    // revenue is linear per meter, so valuing the SUMMED kWp at this blend reproduces
    // the exact Σ-of-per-meter-projections. Using meter[0]'s tariff alone mis-prices
    // any meter on a different voltage band (e.g. LV 0.515 + MV 0.303).
    const combinedTariff = (() => {
        const wKwp = resultList.reduce((s, r) => s + (Number(r.kwp) || 0), 0);
        if (wKwp <= 0) return 0.515;
        const wRev = resultList.reduce((s, r) => s + (Number(r.kwp) || 0) * (Number(r.tariffRate) || 0), 0);
        return wRev / wKwp;
    })();
    // BASE price for the proposal's price block. Add-ons are passed separately as
    // `addOn` so the PDF can print them as their own line rather than burying them.
    // SINGLE SOURCE OF TRUTH: `combined.cash` is the one figure the meter card, the
    // WhatsApp message and the saved sale all quote. The proposal must reconstruct
    // itself FROM that number, not re-derive its own from the parts.
    //
    // The document still shows base and add-on on separate lines, so the base is cash
    // MINUS add-ons — which makes base + add-on ≡ cash by construction. Previously the
    // base was rebuilt independently as panelNet + batteryCost; that happened to equal
    // the same thing, so when add-ons went missing nothing caught it and the customer's
    // PDF sat RM3,000 under the price they had been quoted. (user 2026-08-26)
    const docSystemCost = (combined.cash || 0) - (combined.addons || 0);
    // The MATH, though, must run on the total the customer actually pays — add-ons are
    // real capital, and excluding them made ROI, IRR and LCOE look better than reality.
    // ROI / IRR / LCOE run on the whole capital the customer pays — which is exactly
    // `combined.cash`, add-ons included. Same single source as the line above.
    const mathSystemCost = combined.cash || 0;
    const math = useCscProposalMath({
        kwp: combined.kwp,
        systemCost: mathSystemCost,
        tariffRate: combinedTariff,
        omAnnual: combined.om,
        // Drive the 20-yr projection off the combined CARD saving (per-slot, self-consumption
        // capped) so the proposal tables tally with the calculator — not gross gen × tariff.
        // (user 2026-07-26; mirrors cscCombinedTab.year1NetSaving)
        year1NetSaving: (combined.savingMo || 0) * 12,
        includeCA, includeITA, includeOutright, includeSARE, includeSARE10, includeLoan,
        sareTariffRate: parseFloat(sareTariffOverride) || undefined,
        // Backstop only — the field above already carries the uplift.
        sareAddOnCost: _sareAddOnCost,
        loanPrincipal: parseFloat(loanPrincipal) || undefined,
        loanRate: parseFloat(loanRate) / 100 || 0.07,
        loanTermMonths: parseInt(loanTermMonths, 10) || 120,
    });

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    // Combined "30+40" kWp label for the proposal cover / WhatsApp header.
    const kwpComboLabel = resultList.length > 1 ? resultList.map(r => r.kwp).join('+') : String(combined.kwp);

    // ── Combined Proposal (reuse buildCscVarMap + generateCscProposalPdf) ──
    const buildProposalMap = () => {
        // Per-meter savings breakdown ("RM a + b + c") for the Outright summary cards.
        // 20-year splits proportionally (the math hook is linear in saving). Single → none.
        const _n = (v) => Math.round(Number(v) || 0).toLocaleString('en-US');
        const _sub = (arr) => `<div class="sub" style="font-size:8pt;color:#6B6B6B;font-weight:600;margin-top:1.5mm;">RM ${arr.map(_n).join(' + ')}</div>`;
        const _t20 = (math && math.total20yrOutright) || 0;
        const _totMo = combined.savingMo || 1;
        const savingSplits = resultList.length > 1 ? {
            monthly: _sub(resultList.map(r => r.savingMo || 0)),
            yearly: _sub(resultList.map(r => (r.savingMo || 0) * 12)),
            total20: _sub(resultList.map(r => Math.round(_t20 * ((r.savingMo || 0) / _totMo)))),
        } : undefined;
        return buildCscVarMap({
        customer: { name: customerName, phone: customerPhone, email: customerEmail, address: customerAddress },
        system: {
            kwp: combined.kwp, systemCost: docSystemCost, tariffRate: combinedTariff, tariffKey: (resultList.length > 0 && resultList.every(r => r.tariffKey === resultList[0].tariffKey)) ? resultList[0].tariffKey : undefined,
            // Per-system O&M interval: every meter < 96 kWp → serviced once every 3 years.
            maintenancePeriodYears: (Number(combined.kwp) || 0) > 0 && (Number(combined.kwp) || 0) < 96 ? 3 : 1,   // COMBINED kWp, matching the O&M cost tier (user 2026-08-20)
            omAnnual: combined.om, inverterKw: combined.inverterKw, inverterBrand: brand,
            // Signing-deposit threshold uses the LARGEST single meter's inverter, not the
            // combined sum — 40+42=82 kW stays RM 2,000; one meter >72 kW → RM 5,000. (2026-08-03)
            depositInverterKw: Math.max(0, ...resultList.map(r => Number(r.inverterKw) || 0)),
            inverterBrands: [brand], panelWatt: 650, atapPrices: undefined, phaseMode: sharedPhaseMode,
            // Each meter's REAL inverter brand + type (user 2026-09-22: a Huawei-inverter meter
            // printed as "GoodWe Hybrid" because only the tab-level brand was passed).
            inverterPairs: resultList.map(r => ({ brand: r.brand, type: r.invType })),
            operationWarranty,
        },
        math, sales, lang: proposalLang,
        dates: { issued: todayDMY(), valid: plus30DMY() },
        financing: { loanPrincipal: parseFloat(loanPrincipal) || undefined, includeSARE, includeSARE10, includeOutright, includeLoan },
        // Was hardcoded 0, which is why the PDF printed "+ Add-on RM 0" while the customer
        // was really being charged for a backup box and any catalogue rows. The engine
        // already does subtotal = systemCost + addOn and charges SST on the subtotal, so
        // passing the real figure fixes the price, the SST and the grand total at once.
        addOn: combined.addons,
        site: siteState, photos: sitePhotos,
        maxDemand: resultList.reduce((s, r) => s + (r.maxDemand || 0), 0),
        batteryKwh: combined.battKwh,
        batteryBrand: combined.battBrand,
        summarySavingMo: combined.savingMo,
        // Multi-meter override: show the combined panel count / "30+40" kWp on the cover.
        kwpLabelOverride: kwpComboLabel,
        panelCountOverride: combined.panels,
        // All meters' catalogue add-ons → extra BoM rows on the System Components
        // page. Strip the per-meter "(Meter X)" suffix so the rows read cleanly.
        addons: resultList.flatMap(rr => (rr.addonLineItems || []).map(a => ({
            name: (a.ItemName || a.itemName || a.name || '').replace(/\s*\(Meter\s+[A-Z]\)\s*$/i, '').trim(),
            qty: Number(a.Qty || a.qty || a._qty) || 1,
        }))).filter(a => a.name),
        savingSplits,
    });
    };

    const genProposal = async () => {
        if (!customerName.trim()) { setErr('Customer name is required.'); return; }
        if (!anyResult) { setErr('Enter at least one meter bill.'); return; }
        setErr(''); setBusy(true);
        const map = buildProposalMap();
        const r = await generateCscProposalPdf(map, { lang: proposalLang, includeSARE, includeSARE10, includeOutright, includeLoan, includePhotos: sitePhotos.length > 0, customerName, kwp: kwpComboLabel, systemKwp: combined.kwp });
        if (!r.ok) setErr(r.error);
        setBusy(false);
    };

    const printPreview = async () => {
        if (!customerName.trim()) { setErr('Customer name is required.'); return; }
        if (!anyResult) { setErr('Enter at least one meter bill.'); return; }
        setErr('');
        try {
            const cacheBust = `?v=${Date.now()}`;
            const tplUrl = (proposalLang === 'cn' ? '/templates/astern-proposal-csc-cn.html'
                          : proposalLang === 'my' ? '/templates/astern-proposal-csc-my.html'
                          : '/templates/astern-proposal-csc.html') + cacheBust;
            let res = await fetch(tplUrl);
            let tpl = res.ok ? await res.text() : '';
            const looksLikeTemplate = (s) => /class\s*=\s*["']page(\s|["'])/.test(s);
            if (proposalLang !== 'en' && (!res.ok || !looksLikeTemplate(tpl))) {
                res = await fetch(`/templates/astern-proposal-csc.html?v=${Date.now()}`);
                tpl = res.ok ? await res.text() : '';
            }
            if (!res.ok || !looksLikeTemplate(tpl)) throw new Error(`Failed to fetch template (HTTP ${res.status})`);
            const origin = window.location.origin;
            tpl = tpl
                .split("src='./").join(`src='${origin}/templates/`)
                .split('src="./').join(`src="${origin}/templates/`)
                .split("url('./").join(`url('${origin}/templates/`)
                .split('url("./').join(`url("${origin}/templates/`)
                .split('url(./').join(`url(${origin}/templates/`);
            const map = buildProposalMap();
            let populated = postProcessCscProposal(cscSubstitute(tpl, map), { includeSARE, includeSARE10, includeOutright, includeLoan, includePhotos: sitePhotos.length > 0 });
            const printUi = `
<style>
  .csc-print-fab { position: fixed; bottom: 24px; right: 24px; z-index: 9999; background: linear-gradient(135deg, #1A8CFF, #0F62CC); color: #fff; border: none; border-radius: 999px; padding: 12px 22px; font: 600 14px/1 'Poppins', -apple-system, sans-serif; letter-spacing: 0.02em; box-shadow: 0 8px 20px rgba(15, 98, 204, 0.35); cursor: pointer; display: inline-flex; align-items: center; gap: 8px; }
  .csc-print-fab:hover { transform: translateY(-1px); }
  @media print { .csc-print-fab { display: none !important; } }
</style>
<button class="csc-print-fab" onclick="window.print()" title="Print or Save as PDF">Print / Save PDF</button>`;
            populated = populated.replace('</body>', `${printUi}\n</body>`);
            const safeName = (customerName || 'Customer').replace(/[\/\\:*?"<>|]/g, '').trim();
            const pdfTitle = `${safeName} - ${kwpComboLabel}KWP Astern Commercial Proposal`;
            populated = populated.replace(/<title>[\s\S]*?<\/title>/, `<title>${pdfTitle.replace(/</g, '&lt;')}</title>`);
            const blob = new Blob([populated], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            if (!win) { setErr('Pop-up blocked — allow pop-ups for this site, then click again.'); return; }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            console.error(e);
            setErr(`Preview failed: ${e.message || e}`);
        }
    };

    // ── Combined WhatsApp (reuse buildCscWhatsAppMsg, fed the combined `r` shape) ──
    const buildCombinedWaR = () => {
        if (!anyResult) return null;
        const kwpN = combined.kwp;
        const capitalCost = combined.cash;
        const savingMo = combined.savingMo;
        const savingYr = savingMo * 12;
        const saving10 = Math.round(math?.total10yrOutright || savingYr * 10);
        const saving20 = Math.round(math?.total20yrOutright || savingYr * 20);
        // ROI matches the PDF (CA/GITA cashflow model), not a flat capital-relief shortcut.
        const roi = (math?.outrightBreakevenExactNoIncentives != null) ? math.outrightBreakevenExactNoIncentives : (savingYr > 0 ? capitalCost / savingYr : 0);
        const yieldRate = roi > 0 ? (1 / roi) * 100 : 0;
        const taxPct = kwpN < 60 ? 24 : 38.8;
        const relief = Math.round(capitalCost * (kwpN < 60 ? 0.24 : 0.388));
        const afterTaxCost = capitalCost - relief;
        const roiAfterTax = (math?.outrightBreakevenExact != null) ? math.outrightBreakevenExact : (savingYr > 0 ? afterTaxCost / savingYr : 0);
        const yieldAfterTax = roiAfterTax > 0 ? (1 / roiAfterTax) * 100 : 0;
        const billN = combined.bill;
        const billSavingPct = billN > 0 ? Math.min((savingMo / billN) * 100, 100) : null;
        // Combined header in breakdown form: "*Combined* — (18 + 19) kWp · (16 + 32) kWh".
        // Single meter → plain "18kWp (… panel) + 16kWh Battery" (builder default).
        const combinedLabel = tr(lang, 'Combined', '合计', 'Gabungan');
        const anyBatt = resultList.some(r => Number(r.battKwh) > 0);
        const kwpForm = resultList.length > 1 ? `(${resultList.map(r => r.kwp).join(' + ')}) kWp` : `${combined.kwp} kWp`;
        const battForm = anyBatt
            ? (resultList.length > 1 ? ` · (${resultList.map(r => r.battKwh).join(' + ')}) kWh` : ` · ${combined.battKwh} kWh`)
            : '';
        const headerOverride = resultList.length > 1 ? `*${combinedLabel}* — ${kwpForm}${battForm}` : null;
        return {
            // "30+40" combined kWp header + combined panel/battery.
            kwp: kwpComboLabel, panels: combined.panels, kw: combined.inverterKw, battKwh: combined.battKwh,
            headerOverride,
            // Discount was hardcoded 0, so a multi-meter message could never show one —
            // an Astern Discount reduced the price but the customer saw only the net
            // figure with no indication they had been given anything. The single-meter
            // builder has always shown it. Derived from panel gross vs net rather than a
            // new field: every meter's discount comes off its panel price, so the sum of
            // (panel − panelNet) IS the total discount. (bug 2026-08-26)
            capitalCost,
            capitalCostRaw: capitalCost + Math.max(0, (combined.panel || 0) - (combined.panelNet || 0)),
            discount: Math.max(0, (combined.panel || 0) - (combined.panelNet || 0)),
            maintenance: combined.om, relief, afterTaxCost, taxPct,
            // Maintenance interval is per-system (< 96 kWp → every 3 years). The combined
            // line shows 3-yearly only when EVERY meter is < 96 kWp; any ≥96 kWp meter
            // falls the combined back to yearly (can't mix intervals on one line).
            maintenancePeriodYears: (Number(combined.kwp) || 0) > 0 && (Number(combined.kwp) || 0) < 96 ? 3 : 1,   // COMBINED kWp, matching the O&M cost tier (user 2026-08-20)
            ppk: 0, battCost: combined.batteryCost, rate: combinedTariff,
            tariffLabel: '', bill: billN, billSavingPct,
            company: (customerName || '').trim().toUpperCase(),
            genMode: 'normal', sareRate: 0,
            // Free O&M warranty years for the WhatsApp "N-year Maintenance Warranty" line —
            // follows the Operation Warranty dropdown ("1 Year"/"3 Years …"). (user 2026-08-02)
            omWarrantyYears: warrantyYears(operationWarranty),
            // Whether the clean is included travels WITH the years - WhatsApp used to
            // assume it always was. (user 2026-10-26)
            omCleaning: warrantyHasCleaning(operationWarranty),
            savingMo, savingYr, saving10, saving20,
            roi, roiAfterTax, yieldRate, yieldAfterTax,
            // All meters' catalogue add-ons → shown in the WhatsApp "Package Includes".
            addonItems: resultList.flatMap(rr => (rr.addonLineItems || []).map(a => ({
                name: (a.ItemName || a.itemName || a.name || '').replace(/\s*\(Meter\s+[A-Z]\)\s*$/i, '').trim(),
                qty: Number(a.Qty || a.qty || a._qty) || 1,
            }))).filter(a => a.name),
        };
    };
    // Per-meter breakdown block prepended ABOVE the combined section. One block per
    // meter: name (letter) + bill, proposed kWp + battery, monthly saving. Mirrors
    // cscWhatsApp.js style (label + emoji bullets), tri-lingual.
    const buildPerMeterBlocks = () => {
        if (resultList.length <= 1) return '';
        const L = (en, zh, ms) => tr(lang, en, zh, ms);
        const blocks = resultList.map((r) => {
            const name = `*${meterName(meters[r.index], r.index, lang)}*`;
            const billLine = r.bill > 0 ? ` (${L('Bill', '账单', 'Bil')} ${fmtRM(r.bill)})` : '';
            const battStr = Number(r.battKwh) > 0
                ? ` + ${r.battKwh} kWh ${L('Battery', '电池', 'Bateri')}`
                : '';
            const panelStr = Number(r.panels) > 0 ? ` (${r.panels} ${L('panels', '片', 'panel')})` : '';
            const proposed = `☀ ${L('Proposed', '建议方案', 'Cadangan')}: ${r.kwp} kWp${panelStr}${battStr}`;
            const saving = `💰 ${L('Saving', '节省', 'Penjimatan')}: ${fmtRM(r.savingMo)}/${L('mo', '月', 'bln')}`;
            // Per-meter price is the capital cost WITHOUT SST (r.cash) — SST is applied once on
            // the combined total below, so per-meter lines stay pre-SST to avoid double-counting.
            const priceLine = r.cash > 0 ? `\n⚡ ${L('Price (excl SST)', '价格（不含SST）', 'Harga (tanpa SST)')}: ${fmtRM(r.cash)}` : '';
            return `${name}${billLine}\n${proposed}\n${saving}${priceLine}`;
        });
        return blocks.join('\n\n') + '\n\n';
    };
    const generatedMsg = (() => {
        const r = buildCombinedWaR();
        if (!r) return '';
        return buildPerMeterBlocks() + buildCscWhatsAppMsg(r, lang);
    })();
    const [copied, setCopied] = useState(false);
    const copyText = async (txt) => {
        if (!txt) return;
        try { await navigator.clipboard.writeText(txt); } catch {
            const ta = document.createElement('textarea');
            ta.value = txt; ta.style.cssText = 'position:fixed;opacity:0';
            document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
        }
    };
    const onCopyMsg = async () => { if (!generatedMsg) return; await copyText(generatedMsg); setCopied(true); setTimeout(() => setCopied(false), 2000); };
    const onOpenWA = async () => { if (!generatedMsg) return; await copyText(generatedMsg); window.open('https://web.whatsapp.com/', '_blank'); };

    // ── Payment method + Combined Quotation / Forms ──
    const [payMethod, setPayMethod] = useState('cash');
    const [payTerm, setPayTerm] = useState(24);
    const payTermsFor = (m) => m === 'cc' ? [24, 36, 60] : m === 'loan' ? [36, 60, 84] : [];
    const payFinanceNote = (m) => m === 'loan' ? 'Refer to the effective interest rate charged by the loan party.'
        : '';   // leasing retired - it is a loan (user 2026-10-17)
    const payAdjustedPrice = (cash, m, t) => m === 'cc' ? Math.round((Number(cash) || 0) / (CC_FACTORS[t] || 0.925)) : Math.round(Number(cash) || 0);
    const onPayMethod = (m) => { setPayMethod(m); const terms = payTermsFor(m); if (terms.length && !terms.includes(payTerm)) setPayTerm(terms[0]); };
    const [quoteBusy, setQuoteBusy] = useState(false);
    const [formBusy, setFormBusy] = useState('');

    // Combined commercial payload — same shape as cscCombinedTab.buildCommercialPayload,
    // fed the COMBINED kWp / panels / inverter / battery / price.
    const buildCommercialPayload = () => {
        const cashPrice = combined.cash;
        const c80 = combined.c80, c20 = combined.c20, cSst = combined.cSst, cGrandTotal = combined.grandTotal;
        const isCc = payMethod === 'cc';
        const ccPrice = isCc ? payAdjustedPrice(cashPrice, 'cc', payTerm) : 0;
        const cc80 = isCc ? Math.round(ccPrice * 0.80) : 0;
        const cc20 = isCc ? sstPortion(ccPrice) : 0;
        const ccSst = isCc ? sstOn(ccPrice) : 0;
        // All meters' catalogue add-ons → quotation scope-of-work line items. Keep the
        // per-meter "(Meter X)" suffix so the customer can tell which project each add-on
        // belongs to (combined doc spans several meters). (user 2026-06-29)
        const addOnItems = resultList.flatMap(rr => (rr.addonLineItems || []).map(a => {
            const name = (a.ItemName || a.itemName || a.name || '').trim();
            if (!name) return null;
            // Same integer-Qty trap as the combined tab: a fractional quantity (PerKwp
            // add-ons carry the kWp) cannot convert to Int32 and kills the whole request.
            // One line at the full amount instead — same total. (user 2026-10-20)
            const rawQty = Number(a.Qty || a.qty || a._qty) || 1;
            const unit = Number(a.UnitPrice) || 0;
            const wholeQty = Number.isInteger(rawQty);
            const qty = wholeQty ? rawQty : 1;
            const price = wholeQty ? unit : Math.round(unit * rawQty * 100) / 100;
            return { id: '', name, desc: '', qty, price };
        })).filter(Boolean);
        return {
            addOnItems,
            companyName: (customerName || '').toUpperCase(),
            address: (customerAddress || '').toUpperCase(),
            date: todayDMY(),
            refNo, program: 'ATAP',
            kwp: combined.kwp,
            kwac: combined.inverterKw,
            // Multi-meter breakdown for the quotation scope: show per-meter kWp / inverter kW
            // (80 + 50 + 30.64 kWp · 50 + 40 + 30 kW) alongside the cumulative. Blank for a
            // single meter → quotation falls back to the cumulative figure. (user 2026-07-09)
            kwpBreakdown: resultList.length > 1 ? resultList.map(r => Number(r.kwp).toFixed(2)).join(' + ') : '',
            kwacBreakdown: resultList.length > 1 ? resultList.map(r => Math.round(Number(r.inverterKw))).join(' + ') : '',
            panelQty: combined.panels,
            panelWatt: '650',
            batteryKwh: combined.battKwh,
            batteryBrand: combined.battBrand,
            batteryQty: combined.battKwh > 0 ? (combined.battBrand === 'huawei' ? Math.max(1, Math.round(combined.battKwh / 241)) : (combined.battKwh < 100 ? Math.ceil(combined.battKwh / 16) : 1)) : 0,
            picSalutation,
            picName: (picName || customerName || '').toUpperCase(),
            picMykad,
            picPosition: (picPosition || '').toUpperCase(),
            picCitizenship,
            picMobile: picMobile || customerPhone || '',
            picEmail: (picEmail || customerEmail || '').toLowerCase(),
            companyTin, companyOffice, companyFax,
            companyEmail: (companyEmail || '').toLowerCase(), companyWebsite,
            agentName: 'Leng Rong Chen',
            agentPosition: 'Director',
            operationWarrantyYears: operationWarranty || DEFAULT_WARRANTY_TERM,
            defectWarrantyYears: defectWarranty || '1 set',
            insuranceAmount: parseInt(insuranceAmount, 10) || 10000,
            commercialInverterBrand: brand || 'goodwe',   // quotation scope brand (Huawei prints "Huawei")
            // Each meter's REAL inverter brand + type, e.g. "Huawei String" or "GoodWe Hybrid /
            // Huawei String" — the tab-level brand alone printed "GoodWe Hybrid" for Huawei meters.
            // Same wording as the proposal's inverter line. (user 2026-09-22)
            commercialInverterLabel: (() => {
                const L = { goodwe: 'GoodWe', huawei: 'Huawei', solis: 'Solis' };
                const parts = resultList
                    .map(r => (L[String(r.brand || '').toLowerCase()] ? `${L[String(r.brand).toLowerCase()]} ${String(r.invType || '').toLowerCase() === 'hybrid' ? 'Hybrid' : 'String'}` : null))
                    .filter(Boolean);
                return parts.length ? [...new Set(parts)].join(' / ') : undefined;
            })(),
            commercialPaymentMethod: payMethod === 'cash' ? 'cash'
                : (payTermsFor(payMethod).length > 0 ? `${payMethod}${payTerm}` : payMethod),
            commercialPrice: cashPrice,
            commercialCashPrice: cashPrice,
            commercialPart80: c80,
            commercialPart20: c20,
            commercialSst: cSst,
            commercialGrandTotal: cGrandTotal,
            commercialCcTerm: isCc ? payTerm : 0,
            commercialCc80: cc80,
            commercialCc20: cc20,
            commercialCcSst: ccSst,
            commercialFinanceNote: payFinanceNote(payMethod),
        };
    };

    // Combined create-sales payload — battery + per-meter system folded into line items.
    const [submitBusy, setSubmitBusy] = useState(false);
    const submitGuardRef = useRef(false);
    const buildSalePayload = () => {
        const p = buildCommercialPayload();
        const items = [];
        // Per meter: one battery line + one line per catalogue add-on row + one flat
        // add-on line. The sale's AddOnPrice = Σ line totals, so FinalPrice +
        // AddOnPrice = combined cash (catalogue + flat add-ons are in each r.cash).
        resultList.forEach((r, i) => {
            // The meter's own name, in English: a quotation should not rename its
            // line items because the calculator was switched to Chinese.
            const tag = meterName(meters[r.index ?? i], r.index ?? i, 'en');
            if (r.battKwh > 0) {
                // Huawei (user 2026-09-21) = HV commercial battery in 241-kWh blocks → qty = blocks.
                const _huawei = r.battBrand === 'huawei';
                const _hv = _huawei || Number(r.battKwh) >= 100;   // HV ≥100 kWh = commercial battery (per-kWh, single unit)
                const qty = _huawei ? Math.max(1, Math.round(r.battKwh / 241)) : _hv ? 1 : Math.ceil(r.battKwh / 16);
                const unit = qty > 0 ? Math.round(r.batteryCost / qty) : r.batteryCost;
                items.push({
                    ItemName: `Battery ${r.battKwh}kWh${_hv ? ' HV' : ''}${_huawei ? ' (Huawei)' : ''} (${tag})`,
                    Category: 'Battery', IsBattery: true,
                    Qty: qty, BatteryKwh: r.battKwh, UnitPrice: unit, LineTotal: r.batteryCost, SortOrder: i,
                });
            }
            // Catalogue add-on rows → one line item each (mirror single-meter CSC shape).
            (r.addonLineItems || []).forEach((it, j) => {
                items.push({
                    ItemName: `${it.ItemName} (${tag})`,
                    Category: it.Category, IsBattery: false,
                    Qty: it.Qty, UnitPrice: it.UnitPrice, LineTotal: it.LineTotal, SortOrder: 100 + i * 10 + j,
                });
            });
            // Flat Add-on (RM) field → its own line item.
            const flat = Math.round(Number(r.flatAddOn) || 0);
            if (flat > 0) {
                items.push({
                    ItemName: `Add-on (${tag})`,
                    Category: null, IsBattery: false,
                    Qty: 1, UnitPrice: flat, LineTotal: flat, SortOrder: 200 + i,
                });
            }
        });
        const addOnSum = items.reduce((sum, it) => sum + (Number(it.LineTotal) || 0), 0);
        return {
            PanelQty: combined.panels,
            Energy: combined.kwp,
            ProductCategory: 'COMMERCIAL',
            Phase: sharedPhaseMode === '3phase' ? '3phase' : 'single',
            OriginalPrice: combined.panel,
            FinalPrice: combined.panelNet,
            AddOnPrice: addOnSum,
            DiscountByAstern: 0,
            DiscountByAgent: 0,
            PaymentMethod: payMethod === 'cc' ? `creditCard${payTerm}` : 'CASH',
            DepositOn: null,
            LineItems: items,
            FormPayloadJson: JSON.stringify(p),
            createCustomerRequest: {
                CustomerName: customerName || '',
                PhoneNumber: customerPhone || '',
                Address: customerAddress || '',
                Email: customerEmail || '',
            },
        };
    };

    const doSubmitSale = async (createAnother) => {
        if (submitGuardRef.current) return;
        if (!customerName.trim()) { setErr('Company / customer name is required.'); return; }
        if (!anyResult) { setErr('Enter at least one meter bill.'); return; }
        submitGuardRef.current = true;
        setSubmitBusy(true); setErr('');
        try {
            const base = buildSalePayload();
            const res = await createSales({ ...base, AgentId: user?.userId, CreatedBy: user?.username });
            const newId = res?.data?.salesId;
            const canViewDetail = user?.roleRights?.includes('AllSalesList');
            if (createAnother) alert('Pending sale created. You can create another.');
            else navigate(newId && canViewDetail ? `/accounting/sales/${newId}` : '/sales');
        } catch (e) {
            setErr('Failed to create sale: ' + (e?.response?.data?.message || e?.response?.data || e.message));
        } finally {
            setSubmitBusy(false);
            submitGuardRef.current = false;
        }
    };

    const genQuotation = async () => {
        if (!customerName.trim()) { setErr('Company / customer name is required.'); return; }
        if (!anyResult) { setErr('Enter at least one meter bill.'); return; }
        setErr(''); setQuoteBusy(true);
        try { await generateCommercialQuotation2(buildCommercialPayload()); }
        catch (e) { setErr(`Quotation failed: ${e.message || e}`); }
        finally { setQuoteBusy(false); }
    };
    const runCommercialForm = async (key, fn) => {
        if (!customerName.trim()) { setErr('Company / customer name is required.'); return; }
        if (!anyResult) { setErr('Enter at least one meter bill.'); return; }
        setErr(''); setFormBusy(key);
        try { await fn(buildCommercialPayload()); }
        catch (e) { setErr(`Form failed: ${e.response?.data || e.message || e}`); }
        finally { setFormBusy(''); }
    };

    // ── Combined table row helper ──
    const TableRowCells = ({ label, perMeter, combinedCell, highlight }) => (
        <Box sx={{ display: 'grid', gridTemplateColumns: `160px repeat(${resultList.length}, minmax(110px,1fr)) minmax(150px,1.4fr)`, borderBottom: `1px solid ${COLORS.border}` }}>
            <Box sx={{ p: 1, fontSize: '0.74rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, bgcolor: '#F8FAFC', borderRight: `1px solid ${COLORS.border}` }}>{label}</Box>
            {perMeter.map((v, i) => (
                <Box key={i} sx={{ p: 1, fontSize: '0.78rem', fontWeight: 600, color: COLORS.text, fontFamily: "'JetBrains Mono',monospace", textAlign: 'center', borderRight: `1px solid ${COLORS.border}` }}>{v}</Box>
            ))}
            <Box sx={{ p: 1, fontSize: '0.8rem', fontWeight: 800, color: highlight ? COLORS.success : COLORS.primary, fontFamily: "'JetBrains Mono',monospace", textAlign: 'right', bgcolor: highlight ? 'rgba(16,185,129,0.06)' : 'rgba(26,140,255,0.05)' }}>{combinedCell}</Box>
        </Box>
    );

    // Full-width group header that splits the combined table into sections.
    const GroupRow = ({ title }) => (
        <Box sx={{ p: '7px 12px', fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#475569', bgcolor: '#EEF2F7', borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}`, fontFamily: PP }}>{title}</Box>
    );

    // ── Draft persistence (mirrors the Combined tab) — keep filled data across page
    // navigation. Persists each meter's bill / MD / tariff + the shared customer,
    // options and payment fields; per-meter system details auto-size back from the
    // bill on restore. Cleared on tab close (sessionStorage) or the Clear Data button.
    const draftRestoredRef = useRef(false);
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(CSC_MM_DRAFT_KEY);
            if (raw) {
                const d = JSON.parse(raw);
                const S = (v, set) => { if (v !== undefined) set(v); };
                if (Array.isArray(d.meters) && d.meters.length) {
                    setMeters(d.meters);
                    // keep the module-level id counter ahead of any restored meter id so
                    // a freshly added meter never collides with a restored one.
                    d.meters.forEach(m => { if (Number(m?.id) > _mid) _mid = Number(m.id); });
                }
                S(d.operatingDays, setOperatingDays); S(d.opStart, setOpStart); S(d.opEnd, setOpEnd);
                S(d.dayUsagePct, setDayUsagePct); S(d.brand, setBrand);
                /* AFA restored, superseding the 2026-07-03 "always default to No AFA on
                   load" rule. That was decided when AFA moved only the Original box; it
                   now sets the rate each meter's bill converts to kWh at, so dropping it
                   reopens the draft with different usage - and so a different system - on
                   every meter. Set the module flag too: cscMeterRow reads getAfaOn()
                   through lvFlatSolarRate, not React state. (user 2026-10-26: "AFA
                   changes system size yes") */
                if (d.afaOn !== undefined) { setAfaOn(d.afaOn); setAfaOnState(d.afaOn); }
                S(d.customerName, setCustomerName); S(d.customerPhone, setCustomerPhone);
                S(d.customerEmail, setCustomerEmail); S(d.customerAddress, setCustomerAddress);
                S(d.siteState, setSiteState);
                S(d.includeOutright, setIncludeOutright); S(d.includeSARE, setIncludeSARE); S(d.includeSARE10, setIncludeSARE10);
                S(d.includeLoan, setIncludeLoan); S(d.includeCA, setIncludeCA); S(d.includeITA, setIncludeITA);
                S(d.sareTariffOverride, setSareTariffOverride); S(d.sareTouched, setSareTouched);
                S(d.loanPrincipal, setLoanPrincipal); S(d.loanRate, setLoanRate); S(d.loanTermMonths, setLoanTermMonths);
                S(d.refNo, setRefNo); S(d.companyTin, setCompanyTin); S(d.companyOffice, setCompanyOffice);
                S(d.companyFax, setCompanyFax); S(d.companyEmail, setCompanyEmail); S(d.companyWebsite, setCompanyWebsite);
                S(d.picSalutation, setPicSalutation); S(d.picName, setPicName); S(d.picMykad, setPicMykad);
                S(d.picPosition, setPicPosition); S(d.picCitizenship, setPicCitizenship); S(d.picMobile, setPicMobile); S(d.picEmail, setPicEmail);
                S(d.operationWarranty, setOperationWarranty); S(d.cleaningTouched, setCleaningTouched); S(d.defectWarranty, setDefectWarranty); S(d.insuranceAmount, setInsuranceAmount);
                S(d.payMethod, setPayMethod); S(d.payTerm, setPayTerm);
            }
        } catch { /* ignore corrupt draft */ }
        setTimeout(() => { draftRestoredRef.current = true; }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!draftRestoredRef.current) return;
        try {
            sessionStorage.setItem(CSC_MM_DRAFT_KEY, JSON.stringify({
                meters, operatingDays, opStart, opEnd, dayUsagePct, brand, afaOn,
                customerName, customerPhone, customerEmail, customerAddress, siteState,
                includeOutright, includeSARE, includeSARE10, includeLoan, includeCA, includeITA,
                sareTariffOverride, sareTouched, loanPrincipal, loanRate, loanTermMonths,
                refNo, companyTin, companyOffice, companyFax, companyEmail, companyWebsite,
                picSalutation, picName, picMykad, picPosition, picCitizenship, picMobile, picEmail,
                operationWarranty, cleaningTouched, defectWarranty, insuranceAmount, payMethod, payTerm,
            }));
        } catch { /* quota / serialize errors are non-fatal */ }
    }, [meters, operatingDays, opStart, opEnd, dayUsagePct, brand, afaOn,
        customerName, customerPhone, customerEmail, customerAddress, siteState,
        includeOutright, includeSARE, includeSARE10, includeLoan, includeCA, includeITA,
        sareTariffOverride, sareTouched, loanPrincipal, loanRate, loanTermMonths,
        refNo, companyTin, companyOffice, companyFax, companyEmail, companyWebsite,
        picSalutation, picName, picMykad, picPosition, picCitizenship, picMobile, picEmail,
        operationWarranty, defectWarranty, insuranceAmount, payMethod, payTerm]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: '#F8FAFC', fontFamily: PP }}>

            {/* Clear-data confirm dialog */}
            <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>Clear all data?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontFamily: PP, fontSize: '0.9rem', color: '#475569' }}>
                        This discards every meter and all entered data on this tab. This can't be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setClearDialogOpen(false)} sx={{ fontFamily: PP, textTransform: 'none', color: '#64748B' }}>
                        Cancel
                    </Button>
                    <Button onClick={() => { setClearDialogOpen(false); doClearData(); }} variant="outlined"
                        sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 700, borderColor: '#EF4444', color: '#EF4444' }}>
                        Clear data
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Clear Data button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button size="small" variant="outlined" onClick={() => setClearDialogOpen(true)}
                    sx={{ fontFamily: PP, fontSize: '0.76rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: '#CBD5E1', color: '#64748B', '&:hover': { borderColor: '#EF4444', color: '#EF4444', bgcolor: 'rgba(239,68,68,0.06)' } }}>
                    {tr(lang, 'Clear Data', '清除数据', 'Padam Data')}
                </Button>
            </Box>

            {/* Phase (shared) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Typography sx={{ minWidth: 56, fontSize: '0.72rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP }}>Phase</Typography>
                <ToggleButtonGroup exclusive size="small" value={sharedPhaseMode}
                    onChange={(_, v) => { if (v && setSharedPhaseMode) setSharedPhaseMode(v); }}
                    sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.74rem', fontWeight: 700, textTransform: 'none', py: 0.4, px: 1.6, color: COLORS.sub, borderColor: COLORS.border, borderRadius: '8px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' } }}>
                    <ToggleButton value="single">Single Phase</ToggleButton>
                    <ToggleButton value="3phase">3 Phase</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* ───────── Section 1 — Multi-Meter Sizing ───────── */}
            <SectionHeader title={tr(lang, 'Section 1 — Multi Meter', '第一部分 — 多电表')} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(280px, 1fr) minmax(0, 2.4fr)' }, gap: 2, alignItems: 'start' }}>

                {/* LEFT — shared settings */}
                <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>Company Name</Typography>
                            <TextField fullWidth size="small" value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                                placeholder="customer / company"
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Operating Days / week</Typography>
                            {/* Stepper — reliably editable on mobile. (user 2026-07-03) */}
                            <Box sx={{ display: 'flex', alignItems: 'stretch', height: 40, border: '1px solid #C4CDD5', borderRadius: '8px', overflow: 'hidden' }}>
                                <Button onClick={() => setOperatingDays(d => Math.max(1, (Number(d) || 7) - 1))}
                                    sx={{ minWidth: 46, borderRadius: 0, fontSize: '1.3rem', lineHeight: 1, color: '#334155', borderRight: '1px solid #E2E8F0' }}>−</Button>
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: PP, fontSize: '0.95rem', fontWeight: 600, color: '#0F172A' }}>
                                    {operatingDays}<Typography component="span" sx={{ ml: 0.5, fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP }}>/ week</Typography>
                                </Box>
                                <Button onClick={() => setOperatingDays(d => Math.min(7, (Number(d) || 7) + 1))}
                                    sx={{ minWidth: 46, borderRadius: 0, fontSize: '1.3rem', lineHeight: 1, color: '#334155', borderLeft: '1px solid #E2E8F0' }}>+</Button>
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>Operating Hours <span style={{ color: COLORS.sub, fontWeight: 400 }}>· start – end</span></Typography>
                            {/* Steppers (±1h) — solar window 08:00–18:00; daytime % follows. (user 2026-07-24) */}
                            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                                {[[opStart, setOpStart], [opEnd, setOpEnd]].map(([val, setter], i) => (
                                    <Box key={i} sx={{ display: 'contents' }}>
                                        {i === 1 && <Typography sx={{ color: COLORS.sub, fontFamily: PP }}>–</Typography>}
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'stretch', height: 40, border: '1px solid #C4CDD5', borderRadius: '8px', overflow: 'hidden' }}>
                                            <Button onClick={() => { setter(v => Math.max(i === 0 ? 0 : opStart + 1, (Number(v) || 0) - 1)); setDayUsagePct(''); }}
                                                sx={{ minWidth: 36, borderRadius: 0, fontSize: '1.2rem', lineHeight: 1, color: '#334155', borderRight: '1px solid #E2E8F0' }}>−</Button>
                                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: PP, fontSize: '0.9rem', fontWeight: 600, color: '#0F172A' }}>
                                                {val}<Typography component="span" sx={{ ml: 0.2, fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP }}>:00</Typography>
                                            </Box>
                                            <Button onClick={() => { setter(v => Math.min(i === 0 ? opEnd - 1 : 24, (Number(v) || 0) + 1)); setDayUsagePct(''); }}
                                                sx={{ minWidth: 36, borderRadius: 0, fontSize: '1.2rem', lineHeight: 1, color: '#334155', borderLeft: '1px solid #E2E8F0' }}>+</Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>Daytime usage (%) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· blank = follow hours</span></Typography>
                            {/* Stepper (±5%) — mirrors Operating Days; blank = auto (follows hours). (user 2026-07-24) */}
                            {(() => {
                                const _oh = Math.max(0, opEnd - opStart);
                                const _ov = Math.max(0, Math.min(opEnd, 18) - Math.max(opStart, 8));
                                const _comp = _oh <= 0 ? 100 : Math.max(0, Math.min(100, Math.round(_ov / _oh * 100)));
                                const _eff = String(dayUsagePct).trim() !== '' ? Math.max(0, Math.min(100, parseFloat(dayUsagePct) || 0)) : _comp;
                                // Reverse-sync: a daytime % suggests the operating window (anchor 8:00,
                                // extend evening then morning). Matches the auto hours→% direction. (user 2026-07-25)
                                const syncHrs = (dd) => {
                                    if (dd >= 100) { setOpStart(8); setOpEnd(18); return; }
                                    const total = Math.min(24, Math.round(10 / (Math.max(1, dd) / 100)));
                                    let st = 8, en = 8 + total; if (en > 24) { st = Math.max(0, 8 - (en - 24)); en = 24; }
                                    setOpStart(st); setOpEnd(en);
                                };
                                return (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'stretch', height: 40, border: '1px solid #C4CDD5', borderRadius: '8px', overflow: 'hidden' }}>
                                            <Button onClick={() => { const nv = Math.max(42, _eff - 5); setDayUsagePct(String(nv)); syncHrs(nv); }}
                                                sx={{ minWidth: 46, borderRadius: 0, fontSize: '1.3rem', lineHeight: 1, color: '#334155', borderRight: '1px solid #E2E8F0' }}>−</Button>
                                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: PP, fontSize: '0.95rem', fontWeight: 600, color: '#0F172A' }}>
                                                {_eff}<Typography component="span" sx={{ ml: 0.5, fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP }}>%</Typography>
                                            </Box>
                                            <Button onClick={() => { const nv = Math.min(100, _eff + 5); setDayUsagePct(String(nv)); syncHrs(nv); }}
                                                sx={{ minWidth: 46, borderRadius: 0, fontSize: '1.3rem', lineHeight: 1, color: '#334155', borderLeft: '1px solid #E2E8F0' }}>+</Button>
                                        </Box>
                                        {String(dayUsagePct).trim() !== '' && (
                                            <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                                Manual {_eff}% — <Box component="span" onClick={() => setDayUsagePct('')} sx={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 600 }}>reset to auto</Box> ({_comp}%).
                                            </Typography>
                                        )}
                                    </>
                                );
                            })()}
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>Default Inverter Brand</Typography>
                            <Select fullWidth size="small" value={brand} onChange={(e) => setBrand(e.target.value)}
                                sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                <MenuItem value="goodwe" sx={{ fontFamily: PP }}>GoodWe</MenuItem>
                                <MenuItem value="solis" sx={{ fontFamily: PP }}>Solis (String)</MenuItem>
                                <MenuItem value="huawei" sx={{ fontFamily: PP }}>Huawei (≥100 kWp, String)</MenuItem>
                            </Select>
                            <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>Starting brand for each meter — override per meter in its System Details.</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>AFA Surcharge</Typography>
                            <ToggleButtonGroup size="small" exclusive value={afaOn}
                                onChange={(_, v) => { if (v !== null) { setAfaOn(v); setAfaOnState(v); } }}
                                sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', px: 1.5, py: 0.5, borderRadius: '8px' }, '& .Mui-selected': { bgcolor: `${COLORS.primary} !important`, color: '#fff !important' } }}>
                                <ToggleButton value={true}>With AFA</ToggleButton>
                                <ToggleButton value={false}>No AFA</ToggleButton>
                            </ToggleButtonGroup>
                            <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                {afaOn ? 'AFA surcharge added on top of each bill.' : 'Bills are ex-AFA — Original matches the bill you typed.'}
                            </Typography>
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mt: 1.5 }}>
                        These settings apply to every meter. Each meter is sized independently by the same CSC calculator, then all costs &amp; specs are combined.
                    </Typography>
                </div>

                {/* RIGHT — meter list + combined table */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    {meters.map((m, i) => (
                        <CscMeterRow
                            key={m.id}
                            index={i}
                            meter={m}
                            count={meters.length}
                            lang={lang}
                            operatingDays={Math.min(7, Math.max(1, Number(operatingDays) || 7))}
                            opStart={opStart}
                            opEnd={opEnd}
                            dayUsagePct={dayUsagePct}
                            phaseMode={sharedPhaseMode}
                            brand={brand}
                            onChange={changeMeter}
                            onRemove={removeMeter}
                            onResult={onMeterResult}
                            onLpAnalysis={onMeterLpAnalysis}
                        />
                    ))}
                    <Box>
                        <Button onClick={addMeter} variant="outlined" size="small"
                            sx={{ fontFamily: PP, fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: COLORS.primary, color: COLORS.primary, '&:hover': { borderColor: COLORS.dark, bgcolor: 'rgba(26,140,255,0.06)' } }}>
                            + {tr(lang, 'Add meter', '添加电表')}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* ───────── Combined Summary Table ───────── */}
            {anyResult && (
                <>
                    {/* The download sits WITH the table. The PNG button also lives in
                        the WhatsApp card below, which is right for sharing but hopeless
                        for finding: nobody scrolling this table would look three sections
                        down for its export. (user 2026-10-14) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <SectionHeader title={tr(lang, 'Combined Summary', '合并总览')} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button onClick={onDownloadSummary} disabled={chartSaving} variant="outlined" size="small"
                                sx={{ fontFamily: PP, fontSize: '0.76rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>
                                {chartSaving ? tr(lang, 'Saving…', '保存中…') : tr(lang, 'Download image', '下载图片')}
                            </Button>
                            <Button onClick={onExportSummaryExcel} variant="outlined" size="small"
                                sx={{ fontFamily: PP, fontSize: '0.76rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>
                                {tr(lang, 'Export Excel', '导出 Excel')}
                            </Button>
                        </Box>
                    </Box>
                    {/* Horizontal scroll on narrow screens — the fixed-width meter grid
                        overflowed and got clipped on mobile. Swipe to see all meters. (user 2026-07-03) */}
                    <Box ref={summaryRef} sx={{ bgcolor: '#fff', borderRadius: 2, overflowX: 'auto', overflowY: 'visible', WebkitOverflowScrolling: 'touch', border: `1px solid ${COLORS.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                        {/* Header */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: `160px repeat(${resultList.length}, minmax(110px,1fr)) minmax(150px,1.4fr)`, bgcolor: '#0F172A' }}>
                            <Box sx={{ p: 1, fontSize: '0.72rem', fontWeight: 700, color: '#fff', fontFamily: PP }} />
                            {resultList.map((r, i) => (
                                <Box key={r.index} sx={{ p: 1, fontSize: '0.72rem', fontWeight: 700, color: '#fff', fontFamily: PP, textAlign: 'center', borderLeft: '1px solid rgba(255,255,255,0.15)' }}>{meterName(meters[r.index], r.index, lang)}</Box>
                            ))}
                            <Box sx={{ p: 1, fontSize: '0.78rem', fontWeight: 800, color: '#86EFAC', fontFamily: PP, textAlign: 'right', borderLeft: '1px solid rgba(255,255,255,0.15)' }}>{tr(lang, 'COMBINED', '合计')}</Box>
                        </Box>
                        <GroupRow title={tr(lang, 'System', '系统配置', 'Sistem')} />
                        <TableRowCells label={tr(lang, 'System size', '系统大小')}
                            perMeter={resultList.map(r => `${r.kwp} kWp`)}
                            combinedCell={breakdown(r => r.kwp, combined.kwp, 'kWp')} />
                        <TableRowCells label={tr(lang, 'Panels', '电板')}
                            perMeter={resultList.map(r => `${r.panels}`)}
                            combinedCell={`${breakdown(r => r.panels, combined.panels, '')} × Trina`} />
                        <TableRowCells label={tr(lang, 'Inverter', '逆变器')}
                            perMeter={resultList.map(r => `${r.inverterKw} kW`)}
                            combinedCell={breakdown(r => r.inverterKw, combined.inverterKw, 'kW')} />
                        <TableRowCells label={tr(lang, 'Battery', '电池')}
                            perMeter={resultList.map(r => `${r.battKwh} kWh`)}
                            combinedCell={breakdown(r => r.battKwh, combined.battKwh, 'kWh')} />
                        <GroupRow title={tr(lang, 'Savings & ROI', '节省与回报', 'Penjimatan & ROI')} />
                        <TableRowCells label={tr(lang, 'Monthly saving', '每月节省')}
                            perMeter={resultList.map(r => fmtRM(r.savingMo))}
                            combinedCell={fmtRM(combined.savingMo)} highlight />
                        <TableRowCells label="ROI"
                            perMeter={resultList.map(r => r.roiYears != null ? `${r.roiYears} y` : '—')}
                            combinedCell={combined.roiYears != null ? `${combined.roiYears} ${tr(lang, 'years', '年')}` : '—'} />
                        <GroupRow title={tr(lang, 'Pricing (Cash)', '价格 (现金)', 'Harga (Tunai)')} />
                        <TableRowCells label={tr(lang, 'System base', '基础价')}
                            perMeter={resultList.map(r => fmtRM(r.panel))}
                            combinedCell={fmtRM(combined.panel)} />
                        <TableRowCells label={tr(lang, '+ Battery', '+ 电池')}
                            perMeter={resultList.map(r => fmtRM(r.batteryCost))}
                            combinedCell={fmtRM(combined.batteryCost)} />
                        <TableRowCells label={tr(lang, 'Final (CASH)', '最终价 (CASH)')}
                            perMeter={resultList.map(r => fmtRM(r.cash))}
                            combinedCell={fmtRM(combined.cash)} highlight />
                        <TableRowCells label="80% / Part A"
                            perMeter={resultList.map(r => fmtRM(Math.round(r.cash * 0.80)))}
                            combinedCell={fmtRM(combined.c80)} />
                        <TableRowCells label="20% / Part B"
                            perMeter={resultList.map(r => fmtRM(Math.round(r.cash * 0.20)))}
                            combinedCell={fmtRM(combined.c20)} />
                        <TableRowCells label="SST 6% (on 20%)"
                            perMeter={resultList.map(r => fmtRM2(perMeterSst(r)))}
                            combinedCell={fmtRM2(dispSst)} />
                        <TableRowCells label={tr(lang, 'Grand Total (incl SST)', '含税总额')}
                            perMeter={resultList.map(r => fmtRM2(r.cash + perMeterSst(r)))}
                            combinedCell={fmtRM2(dispGrandTotal)} highlight />
                        <GroupRow title={tr(lang, 'Credit Card Installment', '信用卡分期', 'Ansuran Kad Kredit')} />
                        <TableRowCells label="24M CC"
                            perMeter={resultList.map(r => fmtRM(r.cc24))}
                            combinedCell={fmtRM(combined.cc24)} />
                        <TableRowCells label="36M CC"
                            perMeter={resultList.map(r => fmtRM(r.cc36))}
                            combinedCell={fmtRM(combined.cc36)} />
                        <TableRowCells label="60M CC"
                            perMeter={resultList.map(r => fmtRM(r.cc60))}
                            combinedCell={fmtRM(combined.cc60)} />
                    </Box>

                    {/* Combined load-profile chart (all meters summed into a total) */}
                    {combinedSynth && (
                        <CscLoadProfileChart
                            analysis={combinedSynth}
                            title={_loadShape ? tr(lang, 'Combined Load Profile', '合并用电概况') : tr(lang, 'Combined Load Profile (estimated)', '合并用电概况(估算)')}
                            subtitle={`${resultList.length} ${tr(lang, 'meters', '电表')} · ${Math.round(combined.kwp)} kWp · ${combined.anyAtap ? 'ATAP' : 'SELCo'} · ${_loadShape ? tr(lang, 'uploaded pattern (shared across meters)', '已上传模式(共用)') : tr(lang, 'estimated from bills', '根据账单估算')}`}
                        />
                    )}

                    {/* Combined saving / generation breakdown (same as the normal calculator) */}
                    {anyResult && (() => {
                        const isSelco = !combined.anyAtap;
                        const genPct = combined.generated > 0 ? Math.round(combined.selfConsumed / combined.generated * 100) : 0;
                        const offKwh = isSelco ? combined.curtailed : combined.exported;
                        const offPct = combined.generated > 0 ? Math.round(offKwh / combined.generated * 100) : 0;
                        const hasBatt = combined.battCharge > 0;
                        const L = (label, value, color) => (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, py: 0.15 }}>
                                <Typography sx={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.75)', fontFamily: PP, whiteSpace: 'nowrap' }}>{label}</Typography>
                                <Box sx={{ flex: 1, minWidth: 10, borderBottom: '1px dotted rgba(255,255,255,0.22)', transform: 'translateY(-3px)' }} />
                                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: color || '#fff', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{value}</Typography>
                            </Box>
                        );
                        return (
                            <Box sx={{ background: 'linear-gradient(135deg,#0F766E,#065f46)', borderRadius: 2, p: 2, color: '#fff' }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#86EFAC', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP, textAlign: 'center' }}>{tr(lang, 'Combined Monthly Saving', '合并每月省电费')}</Typography>
                                <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '1.3rem', fontWeight: 800, color: '#4ade80', textAlign: 'center', lineHeight: 1.2 }}>{fmtRM(combined.savingMo)}</Typography>
                                <Box sx={{ mt: 0.6, pt: 0.5, borderTop: '1px solid rgba(74,222,128,0.3)' }}>
                                    {L(tr(lang, 'Self-use saving', '自用省电'), fmtRM(combined.selfUseSaving))}
                                    {L(isSelco ? tr(lang, 'Export credit (curtailed)', '出口收益(弃电)') : tr(lang, 'Export credit (RM 0.20)', '出口收益 (RM 0.20)'), isSelco ? tr(lang, 'no credit', '无收益') : fmtRM(combined.exportCredit), isSelco ? '#FCA5A5' : '#86EFAC')}
                                </Box>
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                    {L(tr(lang, 'Solar-hour self-use', '日照时段自用'), `${combined.selfConsumed.toLocaleString()} kWh · ${genPct}%`)}
                                    {L(isSelco ? tr(lang, 'Off-hour curtailed', '非时段(弃电)') : tr(lang, 'Off-hour export', '非时段出口'), `${offKwh.toLocaleString()} kWh · ${offPct}%`, isSelco ? '#FCA5A5' : '#86EFAC')}
                                    {hasBatt && L(tr(lang, 'Solar → battery (charged)', '太阳能→电池(充电)'), `${combined.battCharge.toLocaleString()} kWh`, '#F9A8D4')}
                                    {hasBatt && L(tr(lang, 'Battery → load (used)', '电池→用电(放电)'), `${combined.battUsed.toLocaleString()} kWh`, '#F9A8D4')}
                                    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.12)', mt: 0.3, pt: 0.4 }}>
                                        {L(tr(lang, 'Total generation', '总发电量'), `${combined.generated.toLocaleString()} kWh`)}
                                        {L(tr(lang, 'Grid import', '仍需电网'), `${combined.importAfterSolar.toLocaleString()} kWh`)}
                                        {L(tr(lang, 'Grid export', '出口电网'), isSelco ? tr(lang, '0 (curtailed)', '0(弃电)') : `${combined.exported.toLocaleString()} kWh`, isSelco ? '#FCA5A5' : '#86EFAC')}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })()}

                    {/* Combined WhatsApp */}
                    <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: '#075e54', p: '10px 16px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 36, height: 36, bgcolor: '#128c7e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>💬</Box>
                            <div>
                                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem', fontFamily: PP }}>{tr(lang, 'Combined Commercial Proposal', '合并商业方案')}</Typography>
                                <Typography sx={{ color: '#80cbc4', fontSize: '0.72rem', fontFamily: PP }}>{resultList.length} {tr(lang, 'meters', '电表')}</Typography>
                            </div>
                        </Box>
                        <Box sx={{ bgcolor: '#e5ddd5', minHeight: 120, p: 1.5 }}>
                            <Box sx={{ bgcolor: '#fff', borderRadius: '0 8px 8px 8px', p: 1.25, maxWidth: '90%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                <Typography component="pre" sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.72rem', lineHeight: 1.45, color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                                    {generatedMsg}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ p: '10px 16px', borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Button onClick={onCopyMsg} disabled={!generatedMsg} variant="outlined" size="small"
                                sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>
                                {copied ? tr(lang, 'Copied', '已复制') : tr(lang, 'Copy Message', '复制信息')}
                            </Button>
                            <Button onClick={onDownloadSummary} disabled={chartSaving} variant="outlined" size="small"
                                sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>
                                {chartSaving ? tr(lang, 'Saving...', '保存中...') : tr(lang, 'Download Summary', '下载总览')}
                            </Button>
                            <Button onClick={onOpenWA} disabled={!generatedMsg} variant="contained" size="small"
                                sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg,#25D366,#128C7E)', boxShadow: 'none', '&:hover': { background: 'linear-gradient(135deg,#20b558,#0f7360)', boxShadow: 'none' } }}>
                                {tr(lang, 'Open WhatsApp', '开启 WhatsApp')}
                            </Button>
                        </Box>
                    </div>
                </>
            )}

            {/* ───────── Section 2 — Proposal ───────── */}
            <SectionHeader title={tr(lang, 'Section 2 — Combined Proposal', '第二部分 — 合并提案')} />

            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 2 }}>Project Info</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Company Name" value={customerName} onChange={setCustomerName} required />
                    <Field label="Director / Contact Name" value={picName} onChange={setPicName} />
                    <Field label="Phone" value={customerPhone} onChange={setCustomerPhone} />
                    <Field label="Email" value={customerEmail} onChange={setCustomerEmail} />
                    <Field label="Address" value={customerAddress} onChange={setCustomerAddress} />
                </Box>

                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Site</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Site State (drives irradiance page)</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={siteState} onChange={(e) => setSiteState(e.target.value)} displayEmpty
                                sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.85rem', color: COLORS.sub }}>— Not specified —</MenuItem>
                                {Object.entries(MY_STATE_GHI).map(([k, v]) => (
                                    <MenuItem key={k} value={k} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                        {v.name} <span style={{ color: COLORS.sub, marginLeft: 6 }}>· {v.ghi.toFixed(1)} kWh/m²/day</span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Site Photos (optional, up to 10)</Typography>
                        <Button variant="outlined" component="label" size="small"
                            sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', borderRadius: '8px' }}>
                            Add photo(s)
                            <input type="file" hidden accept="image/*" multiple
                                onChange={(e) => {
                                    const files = Array.from(e.target.files || []);
                                    if (!files.length) return;
                                    const slotsLeft = Math.max(0, 10 - sitePhotos.length);
                                    const toRead = files.slice(0, slotsLeft);
                                    Promise.all(toRead.map((f) => new Promise((resolve) => {
                                        const fr = new FileReader();
                                        fr.onload = () => resolve(fr.result);
                                        fr.onerror = () => resolve(null);
                                        fr.readAsDataURL(f);
                                    }))).then((urls) => setSitePhotos((prev) => [...prev, ...urls.filter(Boolean)].slice(0, 10)));
                                    e.target.value = '';
                                }} />
                        </Button>
                        <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4, display: 'inline-block', ml: 1 }}>
                            {sitePhotos.length === 0 ? 'No photos — Site Survey page omitted.' : `${sitePhotos.length} / 10 selected`}
                        </Typography>
                        {/* Thumbnails with a remove button. This tab had neither: photos
                            could be added and then only counted — "2 / 10 selected" with no
                            way to see WHICH two or drop one. A wrong photo meant clearing
                            the whole form. The Combined and Proposal tabs already worked
                            this way; this one was simply missing it.
                            (user 2026-10-14: "cant delete image") */}
                        {sitePhotos.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.8, mt: 1, flexWrap: 'wrap' }}>
                                {sitePhotos.map((src, i) => (
                                    <Box key={i} sx={{ position: 'relative', width: 64, height: 64, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
                                        <img src={src} alt={`Site ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <Button size="small" title="Remove this photo"
                                            onClick={() => setSitePhotos((prev) => prev.filter((_, j) => j !== i))}
                                            sx={{ position: 'absolute', top: -4, right: -4, minWidth: 0, width: 20, height: 20, p: 0, fontSize: '0.7rem', backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', borderRadius: '50%', '&:hover': { backgroundColor: 'rgba(0,0,0,0.85)' } }}>
                                            ×
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>

                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Financing</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                    <Field label="SARE Tariff (RM/kWh) — auto from kWp tier + add-ons" value={sareTariffOverride} onChange={(v) => { setSareTariffOverride(v); setSareTouched(true); }} type="number" />
                    <Field label="Loan Principal (RM)" value={loanPrincipal} onChange={setLoanPrincipal} type="number" />
                    <Field label="Loan Rate (% p.a.)" value={loanRate} onChange={setLoanRate} type="number" />
                    <Field label="Loan Term (months)" value={loanTermMonths} onChange={setLoanTermMonths} type="number" />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <FormControlLabel control={<Checkbox size="small" checked={includeOutright} onChange={(e) => setIncludeOutright(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text }}>Include Outright</Typography>} />
                    {/* SARE on by default; disabled only once a real system under 42 kWp exists
                        (kwp=0 = not yet computed → stays on). Gate is on kWp, NOT inverter kW
                        (a >30 kW inverter could pair with only ~20 kWp). (user 2026-08-01) */}
                    {(() => { const _below = (combined.kwp || 0) > 0 && combined.kwp < 42; return (<>
                    <FormControlLabel disabled={_below}
                        control={<Checkbox size="small" checked={includeSARE && !_below} onChange={(e) => { setIncludeSARE(e.target.checked); setSareIncludeTouched(true); }} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: _below ? COLORS.sub : COLORS.text }}>Include SARE 20yr{_below ? ' (≥42 kWp)' : ''}</Typography>} />
                    <FormControlLabel disabled={_below}
                        control={<Checkbox size="small" checked={includeSARE10 && !_below} onChange={(e) => { setIncludeSARE10(e.target.checked); setSareIncludeTouched(true); }} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: _below ? COLORS.sub : COLORS.text }}>Include SARE 10yr{_below ? ' (≥42 kWp)' : ''}</Typography>} />
                    </>); })()}
                    <FormControlLabel control={<Checkbox size="small" checked={includeLoan} onChange={(e) => setIncludeLoan(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text }}>Include Loan</Typography>} />
                    <FormControlLabel control={<Checkbox size="small" checked={includeCA} onChange={(e) => setIncludeCA(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text }}>Include CA</Typography>} />
                    <FormControlLabel disabled={(Number(combined.kwp) || 0) < 40} control={<Checkbox size="small" checked={includeITA && (Number(combined.kwp) || 0) >= 40} onChange={(e) => { setItaTouched(true); setIncludeITA(e.target.checked); }} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: (Number(combined.kwp) || 0) >= 40 ? COLORS.text : COLORS.sub }}>Include GITA{(Number(combined.kwp) || 0) < 40 ? ' · ≥40 kWp only' : ''}</Typography>} />
                </Box>

                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mt: 2.5, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Prepared By</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                    <Box>
                        <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Group</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={selectedGroupId} onChange={(e) => onPickGroup(e.target.value)} displayEmpty
                                sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.85rem', color: COLORS.sub }}>— Select group —</MenuItem>
                                {agentGroups.map(g => (<MenuItem key={g.groupId} value={g.groupId} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>{g.groupName}</MenuItem>))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Sales Agent Name</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={selectedAgentId} onChange={(e) => onPickAgent(e.target.value)} displayEmpty
                                sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.85rem', color: COLORS.sub }}>— Select agent —</MenuItem>
                                {groupAgents.map(a => (<MenuItem key={a.userId} value={a.userId} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>{handleAgentName(a.firstName, a.lastName, a.alias)}</MenuItem>))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <Field label="Agent Position" value={sales.title} disabled />
                    </Box>
                    <Field label="Email" value={sales.email} onChange={(v) => setSales(s => ({ ...s, email: v }))} />
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 1 }}>Generate Combined Proposal</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <Button variant="outlined" disabled={!customerName.trim() || !anyResult} onClick={printPreview}
                            sx={{ fontFamily: PP, fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', borderColor: COLORS.primary, color: COLORS.primary, '&:hover': { borderColor: COLORS.dark, backgroundColor: 'rgba(26,140,255,0.08)' } }}>
                            Print Preview (Ctrl+P → Save as PDF)
                        </Button>
                        <Button variant="contained" disabled={busy || !customerName.trim() || !anyResult} onClick={genProposal}
                            sx={{ fontFamily: PP, fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', '&:hover': { background: 'linear-gradient(135deg,#0F62CC,#0A4FA8)', boxShadow: 'none' } }}>
                            {busy ? 'Generating…' : 'Download PDF'}
                        </Button>
                    </Box>
                </Box>
                {err && <Typography sx={{ color: '#EF4444', fontSize: '0.74rem', fontFamily: PP, mt: 1 }}>{err}</Typography>}
            </div>

            {/* ───────── Section 3 — Quotation & Forms ───────── */}
            <SectionHeader title={tr(lang, 'Section 3 — Quotation & Forms', '第三部分 — 报价单与表格')} />

            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 2 }}>Company / Quotation Details</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Ref No" value={refNo} onChange={setRefNo} />
                    <Field label="Company TIN" value={companyTin} onChange={setCompanyTin} />
                    <Field label="Company Office (Tel)" value={companyOffice} onChange={setCompanyOffice} />
                    <Field label="Company Fax" value={companyFax} onChange={setCompanyFax} />
                    <Field label="Company Email" value={companyEmail} onChange={setCompanyEmail} />
                    <Field label="Company Website" value={companyWebsite} onChange={setCompanyWebsite} />
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>PIC (Person In Charge)</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Salutation (Mr/Ms/Mdm/Dr)" value={picSalutation} onChange={setPicSalutation} />
                    <Field label="Name" value={picName} onChange={setPicName} />
                    <Field label="MyKad / IC" value={picMykad} onChange={setPicMykad} />
                    <Field label="Position" value={picPosition} onChange={setPicPosition} />
                    <Field label="Citizenship" value={picCitizenship} onChange={setPicCitizenship} />
                    <Field label="Mobile" value={picMobile} onChange={setPicMobile} />
                    <Field label="Email" value={picEmail} onChange={setPicEmail} />
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Warranty &amp; Terms</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {/* Two inputs composing one stored value, exactly as the Combined tab —
                        the twins must not drift. (user 2026-10-26: "just the input split") */}
                    <Field label="Operation Warranty (years)"
                        value={warrantyTermOnly(operationWarranty)}
                        onChange={(v) => setOperationWarranty(composeWarranty(v, warrantyCleaningTimes(operationWarranty)))}
                        options={WARRANTY_TERM_OPTIONS} />
                    <Field label="Cleaning"
                        value={cleaningOptionsFor(combined?.kwp)[warrantyCleaningTimes(operationWarranty)] || 'No Cleaning'}
                        onChange={(v) => { setCleaningTouched(true); setOperationWarranty(composeWarranty(warrantyTermOnly(operationWarranty), cleaningOptionsFor(combined?.kwp).indexOf(v))); }}
                        options={cleaningOptionsFor(combined?.kwp)} />
                    <Field label="Defect Warranty" value={defectWarranty} onChange={setDefectWarranty} />
                    <Field label="Insurance Amount (RM)" value={insuranceAmount} onChange={setInsuranceAmount} type="number" />
                </Box>
            </div>

            {/* Payment Method */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 1 }}>Payment Method</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    <ToggleButtonGroup exclusive size="small" value={payMethod} onChange={(_, v) => v && onPayMethod(v)}
                        sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', py: 0.5, px: 1.6, color: COLORS.sub, borderColor: COLORS.border, borderRadius: '6px !important' }, '& .Mui-selected': { background: `${COLORS.primary} !important`, color: '#fff !important', borderColor: `${COLORS.primary} !important` } }}>
                        <ToggleButton value="cash">Cash</ToggleButton>
                        <ToggleButton value="cc">CC</ToggleButton>
                        <ToggleButton value="loan">Loan</ToggleButton>
                    </ToggleButtonGroup>
                    {payTermsFor(payMethod).length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select value={payTerm} onChange={(e) => setPayTerm(Number(e.target.value))}
                                sx={{ fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' }}>
                                {payTermsFor(payMethod).map(t => (<MenuItem key={t} value={t} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>{t} months</MenuItem>))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
                {payMethod === 'cc' && (
                    <Typography sx={{ fontSize: '0.72rem', color: COLORS.text, fontFamily: PP, mt: 1 }}>
                        CC {payTerm}M price: <b>{fmtRM(payAdjustedPrice(combined.cash, 'cc', payTerm))}</b> <span style={{ color: COLORS.sub }}>(cash {fmtRM(combined.cash)} ÷ {CC_FACTORS[payTerm]})</span>
                    </Typography>
                )}
                {payMethod === 'loan' && (
                    <Typography sx={{ fontSize: '0.72rem', color: COLORS.warning, fontFamily: PP, mt: 1, fontStyle: 'italic' }}>{payFinanceNote(payMethod)}</Typography>
                )}
            </div>

            {/* Create Sales + Quotation / Forms */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 0.5 }}>Create Sales</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, mb: 2 }}>
                    Creates ONE combined commercial sale (all meters' costs/specs combined) — download the quotation and forms from the sale page afterwards.
                </Typography>
                {(() => {
                    const disabledBase = !customerName.trim() || !anyResult;
                    const anyRunning = quoteBusy || !!formBusy;
                    const canCreate = user?.roleRights?.includes('CreateSale');
                    const primarySx = { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', '&:hover': { boxShadow: 'none', background: 'linear-gradient(135deg,#0F62CC,#0B4DA3)' }, '&.Mui-disabled': { background: '#E2E8F0', color: '#CBD5E1' } };
                    const outlinedSx = { fontFamily: PP, fontSize: '0.74rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', borderColor: '#CBD5E1', color: '#475569', '&:hover': { borderColor: '#1A8CFF', bgcolor: 'rgba(26,140,255,0.06)' }, '&.Mui-disabled': { borderColor: '#E2E8F0', color: '#CBD5E1' } };
                    return (
                        <>
                            {canCreate ? (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 1 }}>
                                    <Button size="small" variant="contained" disabled={disabledBase || anyRunning || submitBusy} onClick={() => doSubmitSale(false)}
                                        sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#16A34A,#15803D)', '&:hover': { background: 'linear-gradient(135deg,#15803D,#166534)', boxShadow: 'none' } }}>
                                        {submitBusy ? 'Working…' : 'Create Sales'}
                                    </Button>
                                    <Button size="small" variant="outlined" disabled={disabledBase || anyRunning || submitBusy} onClick={() => doSubmitSale(true)}
                                        sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', borderColor: '#16A34A', color: '#16A34A', '&:hover': { borderColor: '#15803D', backgroundColor: 'rgba(22,163,74,0.08)' } }}>
                                        {submitBusy ? 'Working…' : 'Create Sales & Another'}
                                    </Button>
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: '0.74rem', color: COLORS.sub, fontFamily: PP, fontStyle: 'italic', mb: 1 }}>
                                    You don't have permission to create sales.
                                </Typography>
                            )}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(6, 1fr)' }, gap: 1, mt: 1 }}>
                                <Button size="small" variant="contained" disabled={disabledBase || anyRunning} onClick={genQuotation} sx={primarySx}>
                                    {quoteBusy ? 'Working…' : 'Quotation'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('atap', generateCommercialAtap)} sx={outlinedSx}>
                                    {formBusy === 'atap' ? 'Working…' : 'ATAP Contract'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('declaration', generateCommercialDeclaration)} sx={outlinedSx}>
                                    {formBusy === 'declaration' ? 'Working…' : 'Declaration'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('insurance', generateCommercialInsurance2)} sx={outlinedSx}>
                                    {formBusy === 'insurance' ? 'Working…' : 'Insurance'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('booking', generateCommercialBooking)} sx={outlinedSx}>
                                    {formBusy === 'booking' ? 'Working…' : 'Booking'}
                                </Button>
                                <Button size="small" variant="contained" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('all', generateCommercialAll)} sx={primarySx}>
                                    {formBusy === 'all' ? 'Working…' : 'All (ZIP)'}
                                </Button>
                            </Box>
                        </>
                    );
                })()}
                {err && <Typography sx={{ color: '#EF4444', fontSize: '0.74rem', fontFamily: PP, mt: 1.25 }}>{err}</Typography>}
            </div>
        </Box>
    );
}
